import React, { useEffect, useState } from 'react';
import { Box, Stack, FormControl, FormLabel, Input, Button, CircularProgress, Sheet } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import type { Settings as SettingsType, SmtpFingerprint } from '../../api/admin.ts';
import { PageHeader, SectionCard, PageMessage } from '../../components/admin/Page.tsx';
import ChipsInput from '../../components/admin/ChipsInput.tsx';
import SmtpFingerprintEditor from '../../components/admin/SmtpFingerprintEditor.tsx';

const DEFAULT_FINGERPRINT: SmtpFingerprint = {
  hostname: '',
  banner: 'Postfix',
  greetingOverride: '',
  ehloSuffix: '',
  ehloFeatures: [
    'PIPELINING', 'SIZE 10485760', 'ETRN', 'STARTTLS',
    'ENHANCEDSTATUSCODES', '8BITMIME', 'DSN', 'SMTPUTF8', 'CHUNKING'
  ],
  okResponse: '2.0.0 Ok',
  mailAcceptedResponse: '2.1.0 Ok',
  rcptAcceptedResponse: '2.1.5 Ok',
  dataQueuedResponse: '2.0.0 Ok: queued as {id}',
  byeResponse: '2.0.0 Bye',
  startTlsResponse: '2.0.0 Ready to start TLS',
  vrfyCode: 502,
  vrfyMessage: '5.5.1 VRFY command is disabled',
  helpCode: 502,
  helpMessage: '5.5.1 Error: command not implemented',
  tooSoonResponse: '5.5.1 Error: bad syntax',
  unknownCommandResponse: '5.5.2 Error: command not recognized',
  hideXClient: true
};

const DEFAULT: SettingsType = {
  domains: [],
  senderAllows: [],
  rateLimitMaxAttempts: 5,
  rateLimitWindowMinutes: 5,
  smtpFingerprint: DEFAULT_FINGERPRINT
};

export default function Settings() {
  const { api } = useAdmin();
  const [settings, setSettings] = useState<SettingsType>(DEFAULT);
  const [original, setOriginal] = useState<SettingsType>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    api.getSettings()
      .then(s => {
        // Merge in defaults for any missing fingerprint fields (older DB doc).
        const merged: SettingsType = {
          ...s,
          smtpFingerprint: { ...DEFAULT_FINGERPRINT, ...(s.smtpFingerprint || {}) }
        };
        setSettings(merged);
        setOriginal(merged);
      })
      .catch(err => setMessage({ type: 'danger', text: err.message }))
      .finally(() => setLoading(false));
  }, [api]);

  const update = <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const dirty = JSON.stringify(settings) !== JSON.stringify(original);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const updated = await api.updateSettings(settings);
      setSettings(updated);
      setOriginal(updated);
      setMessage({ type: 'success', text: 'Settings saved' });
    } catch (err: any) {
      setMessage({ type: 'danger', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', position: 'relative', pb: 12 }}>
      <PageHeader
        title="Settings"
        subtitle="Configure mailbox domains, sender allow-list and rate limits"
      />
      <PageMessage message={message} />
      <Stack spacing={3}>
        <SectionCard title="Domains" description="Domains accepted for mailbox creation">
          <ChipsInput
            label="Allowed mailbox domains"
            helper="Type or paste comma-separated values"
            values={settings.domains}
            onChange={v => update('domains', v)}
            placeholder="example.com, mail.example.com"
          />
        </SectionCard>

        <SectionCard title="Sender Restrictions" description="Empty list blocks all senders">
          <ChipsInput
            label="Allowed sender domains"
            helper="Comma-separated supported"
            values={settings.senderAllows}
            onChange={v => update('senderAllows', v)}
            placeholder="gmail.com, outlook.com"
          />
        </SectionCard>

        <SectionCard title="Rate Limit" description="Login throttling for mailbox endpoints">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Max attempts</FormLabel>
              <Input type="number" value={settings.rateLimitMaxAttempts}
                onChange={e => update('rateLimitMaxAttempts', Number(e.target.value))} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Window (minutes)</FormLabel>
              <Input type="number" value={settings.rateLimitWindowMinutes}
                onChange={e => update('rateLimitWindowMinutes', Number(e.target.value))} />
            </FormControl>
          </Stack>
        </SectionCard>

        <SectionCard
          title="SMTP Fingerprint"
          description="Disguise the SMTP server identity. Defaults to Postfix to avoid reputation-based shadowbans."
        >
          <SmtpFingerprintEditor
            value={settings.smtpFingerprint}
            onChange={v => update('smtpFingerprint', v)}
          />
        </SectionCard>
      </Stack>

      <Sheet
        variant="plain"
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 3,
          py: 2,
          px: 3,
          mx: -3,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.body',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          zIndex: 5
        }}
      >
        <Button variant="plain" color="neutral" disabled={!dirty || saving}
          onClick={() => setSettings(original)}>Discard</Button>
        <Button onClick={handleSave} loading={saving} disabled={!dirty}>Save Changes</Button>
      </Sheet>
    </Box>
  );
}
